import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import ModuleAccordion from './ModuleAccordion';
import NotificationConfigurationDrawer from './NotificationConfigurationDrawer';
import { ENTITY_TYPE } from 'common/constants';
import { handleAPIError } from 'common/errorHandler';
import { NotificationConfigApiWrapper } from 'components/notifications/Configuration/NotificationConfigApiWrapper';
import type { ModuleActionConfigAndSchemaResponse } from 'axiosApi/models/module-action-config-and-schema-response';
import { NotificationConfigDTO } from 'axiosApi/models';
import { useNavigate } from 'react-router';

/**
 * NotificationConfiguration - Main page component for configuring notification settings
 *
 * Data Flow:
 * 1. Load data from NotificationApiWrapper
 * 2. Render modules as expandable accordions
 * 3. Each module has a toggle (active/inactive) + action checkboxes
 * 4. Clicking an action label opens the drawer with configuration options
 * 5. Drawer shows: priority, recipients, scopes, supervisor
 * 6. Confirm button stores changes in pending state (no API call)
 * 7. Discard button reverts to last confirmed state or original state
 * 8. Save All button persists all pending changes to API
 *
 * State Management (Three-Tier System):
 * - originalConfigs: Initial state loaded from API
 * - pendingConfigs: Confirmed changes not yet saved to API
 * - Drawer state: Temporary changes while drawer is open
 *
 * Flow:
 * - API → originalConfigs (on mount)
 * - Confirm → pendingConfigs (no API call, marks hasUnsavedChanges)
 * - Discard → revert to pendingConfigs or originalConfigs
 * - Save All → API call with pendingConfigs, then update originalConfigs
 *
 * Additional State:
 * - modulesData: Full data structure from API
 * - selectedActions: Map<string, boolean> - key format: "moduleName.actionName"
 * - moduleActiveStates: Map<string, boolean> - key format: "moduleName"
 * - hasUnsavedChanges: Boolean flag to enable/disable Save All button
 */
const NotificationConfiguration: React.FC = () => {
  const toast = useToastMessageQueue();
  const api: Api = useApi();
  const navigate = useNavigate();
  const notificationConfigApiWrapper = new NotificationConfigApiWrapper(api);

  // Core data - using generated API types
  const [modulesData, setModulesData] =
    useState<ModuleActionConfigAndSchemaResponse['modules']>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState({});

  // Entity lists (fetched from API)
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [allProviders, setAllProviders] = useState<any[]>([]);
  const [allCorporateEntities, setAllCorporateEntities] = useState<any[]>([]);
  const [allScopes, setAllScopes] = useState<any[]>([]);

  // Selected entities for current action configuration
  const [selectedEmployees, setSelectedEmployees] = useState<any[]>([]);
  const [selectedClients, setSelectedClients] = useState<any[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<any[]>([]);
  const [selectedCorporateEntities, setSelectedCorporateEntities] = useState<any[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<any[]>([]);
  // Selection state
  const [selectedActions, setSelectedActions] = useState<Map<string, boolean>>(new Map());
  const [moduleActiveStates, setModuleActiveStates] = useState<Map<string, boolean>>(new Map());

  // Store original selection states to detect changes
  const [originalSelectedActions, setOriginalSelectedActions] = useState<Map<string, boolean>>(
    new Map()
  );
  const [originalModuleActiveStates, setOriginalModuleActiveStates] = useState<
    Map<string, boolean>
  >(new Map());

  // Configuration state management
  // originalConfigs: The initial state loaded from API
  // pendingConfigs: Confirmed changes that haven't been saved to API yet
  // currentDrawerState: Temporary state while drawer is open (not used here, managed in drawer)
  const [originalConfigs, setOriginalConfigs] = useState<Map<string, NotificationConfigDTO>>(
    new Map()
  );
  const [pendingConfigs, setPendingConfigs] = useState<Map<string, NotificationConfigDTO>>(
    new Map()
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<{
    moduleName: string;
    actionName: string;
  } | null>(null);

  // Navigation blocker state
  const [showNavigationDialog, setShowNavigationDialog] = useState<boolean>(false);
  const [nextLocation, setNextLocation] = useState<string | null>(null);

  /**
   * Load all data on mount: entity lists + module config
   */
  useEffect(() => {
    loadAllData();
  }, []);

  /**
   * Navigation blocker - Intercept link clicks when there are unsaved changes
   * Includes config changes, checkbox changes, and module toggle changes
   */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!hasAnyUnsavedChanges()) return;

      // Find if click was on a link or inside a link
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        const linkUrl = new URL(link.href);
        const currentUrl = new URL(window.location.href);

        // Check if it's an internal navigation (same origin)
        if (linkUrl.origin === currentUrl.origin && linkUrl.pathname !== currentUrl.pathname) {
          e.preventDefault();
          e.stopPropagation();
          setNextLocation(linkUrl.pathname + linkUrl.search + linkUrl.hash);
          setShowNavigationDialog(true);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasUnsavedChanges, selectedActions, moduleActiveStates, originalSelectedActions, originalModuleActiveStates]);

  /**
   * Load all data: entity lists + module configuration
   */
  const loadAllData = async () => {
    try {
      setLoading(true);

      // Fetch all entity lists and module config in parallel
      const [entitiesData, configData] = await Promise.all([
        notificationConfigApiWrapper.fetchAllEntities('1'),
        notificationConfigApiWrapper.getEntireConfig(),
      ]);

      // Set entity lists
      setAllEmployees(entitiesData.employees || []);
      setAllClients(entitiesData.clients || []);
      setAllProviders(entitiesData.providers || []);
      setAllCorporateEntities(entitiesData.corporateEntities || []);
      setAllScopes(entitiesData.scopes || []);

      // Set module configuration
      // configData.modules can be null or undefined, so use empty object as fallback
      const modules = configData.modules || {};
      setModulesData(modules);

      // Initialize module active states and selected actions based on existing configs
      const newModuleStates = new Map<string, boolean>();
      const newSelectedActions = new Map<string, boolean>();
      const initialConfigs = new Map<string, NotificationConfigDTO>();

      Object.entries(modules).forEach(([moduleName, actions]) => {
        // Skip if actions is null
        if (!actions) return;

        // Check if ALL actions in this module are active (based on isActive property)
        // Module is active only when all its actions are checked
        const allActionsActive = Object.values(actions).every((action) => {
          const isActiveFromSchema = (action.schema as any)?.isActive === "True";
          const isActiveFromConfig = (action.config as any)?.isActive === true;
          return isActiveFromSchema || isActiveFromConfig;
        });
        newModuleStates.set(moduleName, allActionsActive);

        // Mark actions as selected based on isActive status
        Object.entries(actions).forEach(([actionName, action]) => {
          const key = `${moduleName}.${actionName}`;
          
          // Check if action is active from either schema or config
          // schema.isActive is a string: "True" or "False"
          // config.isActive is a boolean: true or false
          const isActiveFromSchema = (action.schema as any)?.isActive === "True";
          const isActiveFromConfig = (action.config as any)?.isActive === true;
          const isActive = isActiveFromSchema || isActiveFromConfig;
          
          newSelectedActions.set(key, isActive);

          // Store the original config if it exists
          if (action.config) {
            initialConfigs.set(key, action.config);
          }
        });
      });

      setModuleActiveStates(newModuleStates);
      setSelectedActions(newSelectedActions);

      // Store original states for change detection
      setOriginalSelectedActions(new Map(newSelectedActions));
      setOriginalModuleActiveStates(new Map(newModuleStates));

      // Initialize both original and pending with the same data
      setOriginalConfigs(initialConfigs);
      setPendingConfigs(new Map(initialConfigs)); // Create a copy
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to load notification configuration:', error);
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle module active/inactive state
   * When toggling, also update all action checkboxes under this module
   */
  const handleToggleModuleActive = (moduleName: string, active: boolean) => {
    setModuleActiveStates((prev) => {
      const updated = new Map(prev);
      updated.set(moduleName, active);
      return updated;
    });
  };

  /**
   * Handle action checkbox selection
   * Updates the isActive property in the config
   */
  const handleActionSelect = (moduleName: string, actionName: string, selected: boolean) => {
    const key = `${moduleName}.${actionName}`;
    
    // Update checkbox state
    setSelectedActions((prev) => {
      const updated = new Map(prev);
      updated.set(key, selected);

      if (modulesData[moduleName]) {
        const allActions = Object.keys(modulesData[moduleName]);
        const allSelected = allActions.every((action) => {
          const actionKey = `${moduleName}.${action}`;
          if (actionKey === key) {
            return selected;
          }
          return updated.get(actionKey) || false;
        });

        setModuleActiveStates((prevModuleStates) => {
          const updatedModuleStates = new Map(prevModuleStates);
          updatedModuleStates.set(moduleName, allSelected);
          return updatedModuleStates;
        });
      }

      return updated;
    });

    // Update the config's isActive property
    setPendingConfigs((prev) => {
      const updated = new Map(prev);
      const existingConfig = updated.get(key) || originalConfigs.get(key);
      
      if (existingConfig) {
        // Update existing config
        updated.set(key, {
          ...existingConfig,
          isActive: selected,
        } as NotificationConfigDTO & { isActive?: boolean });
      } else {
        // Create minimal config if action doesn't have one yet
        updated.set(key, {
          module: moduleName,
          priority: null,
          recipients: {
            entities: null,
            scopes: null,
            supervisor: null,
          },
          isActive: selected,
        } as NotificationConfigDTO & { isActive?: boolean });
      }
      
      return updated;
    });

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  /**
   * Handle action click - opens drawer with selected entities
   * Always uses pending config (last confirmed state) if available
   */
  const handleActionClick = (moduleName: string, actionName: string) => {
    setCurrentAction({ moduleName, actionName });

    // Get current action's config to pre-select entities
    // Priority: pendingConfigs (last confirmed) > originalConfigs (from API)
    const key = `${moduleName}.${actionName}`;
    const config = pendingConfigs.get(key) || originalConfigs.get(key) || null;

    // Parse and filter selected entities by type
    // Handle both PascalCase (API response) and camelCase (TypeScript models)
    const recipients = (config as any)?.Recipients || config?.recipients;
    const entitiesArray = recipients?.Entities || recipients?.entities || [];

    // Helper to get entity property (handle both PascalCase and camelCase)
    const getEntityType = (e: any) => e.EntityType || e.entityType;
    const getEntityId = (e: any) => e.EntityId || e.entityId;

    const employeeEntities = entitiesArray.filter(
      (e) => getEntityType(e) === String(ENTITY_TYPE.EMPLOYEE)
    );
    const clientEntities = entitiesArray.filter((e) => getEntityType(e) === String(ENTITY_TYPE.CLIENT));
    const providerEntities = entitiesArray.filter(
      (e) => getEntityType(e) === String(ENTITY_TYPE.PROVIDER)
    );
    const corporateEntityEntities = entitiesArray.filter(
      (e) => getEntityType(e) === String(ENTITY_TYPE.CORPORATE_ENTITY)
    );

    // Match entityId with full entity objects from lists
    const selectedEmps = allEmployees.filter((emp) =>
      employeeEntities.some((e) => getEntityId(e) === String(emp.id))
    );
    const selectedClnts = allClients.filter((client) =>
      clientEntities.some((e) => getEntityId(e) === String(client.id))
    );
    const selectedProvs = allProviders.filter((provider) =>
      providerEntities.some((e) => getEntityId(e) === String(provider.id))
    );
    const selectedCorps = allCorporateEntities.filter((corp) =>
      corporateEntityEntities.some((e) => getEntityId(e) === String(corp.id))
    );

    // Filter scopes: Handle both PascalCase (API) and camelCase (models)
    const scopesArray = recipients?.Scopes || recipients?.scopes || [];
    const selectedScopes = allScopes.filter((scopeObj) => {
      return scopesArray.some((scopeString) => scopeString === scopeObj.scope);
    });

    setSelectedEmployees(selectedEmps);
    setSelectedClients(selectedClnts);
    setSelectedProviders(selectedProvs);
    setSelectedCorporateEntities(selectedCorps);
    setSelectedScopes(selectedScopes); // Use filtered scopes, not raw config
    setDrawerOpen(true);
  };

  /**
   * Handle drawer close
   */
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setCurrentAction(null);
  };

  /**
   * Handle configuration confirm from drawer
   * Stores changes in pendingConfigs without making API call
   */
  const handleConfigConfirm = (
    moduleName: string,
    actionName: string,
    config: NotificationConfigDTO
  ) => {
    const key = `${moduleName}.${actionName}`;

    // Store in pending configs (confirmed but not saved to API)
    setPendingConfigs((prev) => {
      const updated = new Map(prev);
      updated.set(key, config);
      return updated;
    });

    // Mark action as selected
    setSelectedActions((prev) => {
      const updated = new Map(prev);
      updated.set(key, true);
      return updated;
    });

    // Check if there are any changes compared to original
    const hasChanges = checkForChanges(key, config);
    if (hasChanges) {
      setHasUnsavedChanges(true);
    }

    toast.success({
      body:
        intl.get('notification.configuration.confirmed') ||
        `Configuration confirmed for ${actionName}`,
    });
  };

  /**
   * Handle configuration discard from drawer
   * Reverts to last pending state (confirmed) or original state if no pending changes
   */
  const handleConfigDiscard = (moduleName: string, actionName: string) => {
    const key = `${moduleName}.${actionName}`;

    // Get the last pending or original config
    const lastSavedConfig = pendingConfigs.get(key) || originalConfigs.get(key);

    // Update the selected entities to reflect the reverted state
    // This will be used when the drawer reopens
    if (lastSavedConfig) {
      // The drawer will automatically load from pendingConfigs when reopened
      toast.info({ body: intl.get('notification.configuration.discarded') || 'Changes discarded' });
    }
  };

  /**
   * Helper function to check if config differs from original
   */
  const checkForChanges = (key: string, newConfig: NotificationConfigDTO): boolean => {
    const originalConfig = originalConfigs.get(key);

    // If no original config exists, this is a new configuration
    if (!originalConfig && newConfig) return true;

    // Deep comparison (simplified - you might want to use a library like lodash)
    return JSON.stringify(originalConfig) !== JSON.stringify(newConfig);
  };

  /**
   * Helper function to check if selection states have changed
   * Returns true if checkboxes or module toggles differ from original
   */
  const hasSelectionChanges = (): boolean => {
    // Check if selectedActions changed
    if (selectedActions.size !== originalSelectedActions.size) return true;
    for (const [key, value] of selectedActions) {
      if (originalSelectedActions.get(key) !== value) return true;
    }

    // Check if moduleActiveStates changed
    if (moduleActiveStates.size !== originalModuleActiveStates.size) return true;
    for (const [key, value] of moduleActiveStates) {
      if (originalModuleActiveStates.get(key) !== value) return true;
    }

    return false;
  };

  /**
   * Check if there are any unsaved changes (configs, checkboxes, or module toggles)
   */
  const hasAnyUnsavedChanges = (): boolean => {
    return hasUnsavedChanges || hasSelectionChanges();
  };

  /**
   * Handle navigation dialog - Cancel (Stay on page)
   */
  const handleNavigationCancel = () => {
    setShowNavigationDialog(false);
    setNextLocation(null);
  };

  /**
   * Handle navigation dialog - Confirm (Leave page)
   */
  const handleNavigationConfirm = () => {
    setShowNavigationDialog(false);
    if (nextLocation) {
      navigate(nextLocation);
    }
  };

  /**
   * Handle global save - persist all configurations to API
   * IMPORTANT: Sends ALL configs (original + modified) to preserve existing data
   * On success, updates originalConfigs and clears unsaved changes flag
   */
  const handleGlobalSave = async () => {
    try {
      setLoading(true);

      // Build configuration object in the format expected by UpdateEntireConfig
      // Structure: { [moduleName]: { [actionName]: NotificationConfigDTO } }
      const configuration: { [key: string]: { [key: string]: NotificationConfigDTO } } = {};

      // STEP 1: First, include ALL existing configs from modulesData to preserve everything
      if (modulesData) {
        Object.entries(modulesData).forEach(([moduleName, actions]) => {
          if (!actions) return;
          
          Object.entries(actions).forEach(([actionName, action]) => {
            if (action?.config) {
              if (!configuration[moduleName]) {
                configuration[moduleName] = {};
              }
              configuration[moduleName][actionName] = action.config;
            }
          });
        });
      }

      // STEP 2: Then, override with any changes from pendingConfigs
      pendingConfigs.forEach((config, key) => {
        // Split on first dot only to handle action names with dots (e.g., "Client.client.create")
        const dotIndex = key.indexOf('.');
        if (dotIndex === -1) {
          console.warn(`Invalid key format: ${key}`);
          return;
        }
        
        const moduleName = key.substring(0, dotIndex);
        const actionName = key.substring(dotIndex + 1);
        
        if (!configuration[moduleName]) {
          configuration[moduleName] = {};
        }
        configuration[moduleName][actionName] = config;
      });

      const payload = {
        configuration
      };

      await notificationConfigApiWrapper.saveModuleConfig('1', payload);

      // On success, update originalConfigs to include all configs
      const newOriginalConfigs = new Map<string, NotificationConfigDTO>();
      Object.entries(configuration).forEach(([moduleName, actions]) => {
        Object.entries(actions).forEach(([actionName, config]) => {
          const key = `${moduleName}.${actionName}`;
          newOriginalConfigs.set(key, config);
        });
      });
      setOriginalConfigs(newOriginalConfigs);
      setPendingConfigs(new Map(newOriginalConfigs)); // Reset pending to match new original
      
      // Update original selection states to match current states
      setOriginalSelectedActions(new Map(selectedActions));
      setOriginalModuleActiveStates(new Map(moduleActiveStates));
      
      setHasUnsavedChanges(false);

      toast.success({
        body: intl.get('notificationConfig.saveSuccess') || 'Configuration saved successfully',
      });
    } catch (error) {
      console.error('Failed to save configuration:', error);
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  // Get current action's schema and config for drawer
  const getCurrentActionData = () => {
    if (!currentAction) {
      return { schema: null, config: null };
    }

    // Check if module exists
    if (!modulesData || !modulesData[currentAction.moduleName]) {
      console.warn(`Module "${currentAction.moduleName}" not found in modulesData`);
      return { schema: null, config: null };
    }

    // Check if action exists within module
    const action = modulesData[currentAction.moduleName][currentAction.actionName];
    if (!action) {
      console.warn(`Action "${currentAction.actionName}" not found in module "${currentAction.moduleName}"`);
      return { schema: null, config: null };
    }

    // Check if action has schema
    if (!action.schema) {
      console.warn(`Action "${currentAction.actionName}" in module "${currentAction.moduleName}" has no schema`);
      return { schema: null, config: null };
    }

    const key = `${currentAction.moduleName}.${currentAction.actionName}`;

    // Use pending config (last confirmed) if exists, otherwise use original
    const config = pendingConfigs.get(key) || originalConfigs.get(key) || null;

    return {
      schema: action.schema,
      config,
    };
  };

  const { schema, config } = getCurrentActionData();

  return (
    <Box sx={{ p: 3 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography color="primary" variant="h2" component="h2" sx={{ mb: 1 }}>
              {intl.get('notification.configuration.title')}
            </Typography>
            <Typography  color="text.secondary">
              {intl.get('notification.configuration.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="medium"
            startIcon={<SaveIcon />}
            onClick={handleGlobalSave}
            disabled={loading || !hasAnyUnsavedChanges()}
            color={hasAnyUnsavedChanges() ? 'primary' : 'inherit'}
          >
            {intl.get('save')}
            {hasAnyUnsavedChanges() && ' *'}
          </Button>
        </Box>

        {/* Unsaved Changes Alert */}
        {hasAnyUnsavedChanges() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>
              {intl.get('notification.configuration.unsavedChanges') || 'You have unsaved changes.'}
            </strong>{' '}
            {intl.get('notification.configuration.unsavedChangesMessage') ||
              'Click "Save All" to persist your changes to the server.'}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Module Accordions */}
        {!loading && (
          <>
            {/* Modules Header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {intl.get('common.modules') || 'Modules'}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {modulesData &&
                Object.entries(modulesData)
                  .filter(([moduleName]) => moduleName !== 'default') // Filter out immutable default module
                  .map(([moduleName, actions]) => (
                    <ModuleAccordion
                      key={moduleName}
                      moduleName={moduleName}
                      actions={actions}
                      isActive={moduleActiveStates.get(moduleName) || false}
                      onToggleActive={handleToggleModuleActive}
                      selectedActions={selectedActions}
                      onActionSelect={handleActionSelect}
                      onActionClick={handleActionClick}
                    />
                  ))}

              {(!modulesData || Object.keys(modulesData).length === 0) && (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {intl.get('module.notFound') || 'No modules found'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </>
        )}

        {/* Configuration Drawer */}
        <NotificationConfigurationDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          onConfirm={handleConfigConfirm}
          onDiscard={handleConfigDiscard}
          moduleName={currentAction?.moduleName || null}
          actionName={currentAction?.actionName || null}
          schema={schema}
          config={config}
          allEmployees={allEmployees}
          allClients={allClients}
          allProviders={allProviders}
          allCorporateEntities={allCorporateEntities}
          allScopes={allScopes}
          initialSelectedEmployees={selectedEmployees}
          initialSelectedClients={selectedClients}
          initialSelectedProviders={selectedProviders}
          initialSelectedCorporateEntities={selectedCorporateEntities}
          initialSelectedScopes={selectedScopes}
        />

        {/* Navigation Blocker Dialog */}
        <Dialog
          open={showNavigationDialog}
          onClose={handleNavigationCancel}
          aria-labelledby="unsaved-changes-dialog-title"
          aria-describedby="unsaved-changes-dialog-description"
        >
          <DialogTitle id="unsaved-changes-dialog-title">
            {intl.get('workflowEditor.unsavedChanges.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="unsaved-changes-dialog-description">
              {intl.get('workflowEditor.unsavedChanges.message')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNavigationCancel} color="primary">
              {intl.get('workflowEditor.unsavedChanges.stay')}
            </Button>
            <Button onClick={handleNavigationConfirm} color="error" variant="contained" autoFocus>
              {intl.get('workflowEditor.unsavedChanges.leave')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default NotificationConfiguration;
